// Виджет «Закрытие» для таблицы распределения — портирован из статистики
// (src/pages/statistics/closeTimeline.mjs). Полоска от создания карточки
// до закрытия/текущего момента относительно плановой погрузки.
//
// Отличие входных данных: в распределении create_dt/closed_at приходят
// ISO-строками с зоной (+00:00), а planed_date — naive-ISO (хранится
// wall-clock без зоны) → toTs дописывает 'Z', чтобы арифметика часов
// не уезжала на offset локали браузера.

export const HOUR_MS = 60 * 60 * 1000;

export const TIMING_LABELS = {
  'tl-early': 'зелёный — закрыт не менее чем за 24 ч до погрузки',
  'tl-lastday': 'оранжевый — закрыт за 12–24 ч до погрузки',
  'tl-late': 'красный — закрыт менее чем за 12 ч до погрузки (или после)',
  'tl-neutral': 'серый — не закрыт / срок неизвестен',
};

export const TIMING_OPEN_LABELS = {
  'tl-early': 'зелёная штриховка — не закрыт, до погрузки более 24 ч',
  'tl-lastday': 'оранжевая штриховка — не закрыт, до погрузки 12–24 ч',
  'tl-late': 'красная штриховка — не закрыт, до погрузки менее 12 ч / просрочено',
  'tl-neutral': 'серая штриховка — не закрыт, срок неизвестен',
};

const ZONE_RE = /(?:Z|[+-]\d{2}:?\d{2})$/i;

export function toTs(value, { assumeUtc = false } = {}) {
  if (value === null || value === undefined || value === '' || value !== value) return null;
  let normalized = value;
  if (typeof normalized === 'number' || /^\d+$/.test(normalized)) {
    const epoch = Number(normalized);
    normalized = epoch > 1e11 ? epoch : epoch * 1000;
  } else if (assumeUtc && typeof normalized === 'string' && !ZONE_RE.test(normalized)) {
    // naive wall-clock (хранится как UTC): дописываем зону для парсинга
    normalized += 'Z';
  }
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date.getTime();
}

export function timingCls(closed, load, now = Date.now()) {
  if (load == null) return 'tl-neutral';
  if (closed != null) {
    const hours = (load - closed) / HOUR_MS;
    if (hours >= 24) return 'tl-early';
    if (hours >= 12) return 'tl-lastday';
    return 'tl-late';
  }
  const hoursLeft = (load - now) / HOUR_MS;
  if (hoursLeft > 24) return 'tl-neutral';
  if (hoursLeft >= 12) return 'tl-lastday';
  return 'tl-late';
}

function fmt(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function loadingPlanTs(row) {
  const point = row?.order?.route?.points?.[0];
  return toTs(point?.planed_date, { assumeUtc: true });
}

// Модель виджета для карточки распределения:
// { barPct, barCls, markPct, mark12Pct, tooltip } либо null.
export function closeTimelineModel(row, { now = Date.now() } = {}) {
  if (!row) return null;
  const start = toTs(row.create_dt);
  if (start == null) return null;
  const closed = toTs(row.closed_at);
  const load = loadingPlanTs(row);
  const barEnd = closed ?? now;
  const end = load ?? barEnd;
  if (end < start) return null;
  const span = Math.max(end - start, HOUR_MS);
  const pct = (t) => Math.min(100, Math.max(0, ((t - start) / span) * 100));
  const cls = timingCls(closed, load, now);
  const hoursAtClose = closed != null && load != null
    ? ((load - closed) / HOUR_MS).toLocaleString('ru-RU', { maximumFractionDigits: 1 })
    : null;
  const hoursLeft = closed == null && load != null
    ? ((load - now) / HOUR_MS).toLocaleString('ru-RU', { maximumFractionDigits: 1 })
    : null;
  const tooltip = [
    `Создание: ${fmt(start)}`,
    `Закрытие: ${closed != null ? fmt(closed) : '— (в работе)'}`,
    `Погрузка: ${load != null ? fmt(load) : '—'}`,
    hoursAtClose != null ? `Часов до погрузки при закрытии: ${hoursAtClose}` : null,
    hoursLeft != null ? `Часов до погрузки (осталось): ${hoursLeft}` : null,
    closed != null ? TIMING_LABELS[cls] : TIMING_OPEN_LABELS[cls],
    '| — за 24 ч до погрузки · ¦ — за 12 ч · правый край — погрузка',
  ]
    .filter(Boolean)
    .join('\n');
  return {
    barPct: pct(barEnd),
    barCls: closed != null ? cls : `${cls} tl-open`,
    markPct: load != null ? pct(load - 24 * HOUR_MS) : null,
    mark12Pct: load != null ? pct(load - 12 * HOUR_MS) : null,
    tooltip,
  };
}
